import axios from 'axios';
import { TimezonePreferences, DEFAULT_TIMEZONE_PREFERENCES, validateTimezonePreferences } from './timezone';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface UserPreferences {
  passwordProtection?: {
    granted: boolean;
    grantedAt: Date;
  };
  momentum?: {
    lastSeenStatus?: string;
  };
  checkInCompletion?: {
    morning?: boolean;
    evening?: boolean;
    date?: string;
  };
  timezone?: TimezonePreferences;
}

export interface OnboardingData {
  answers?: any[];
  currentStep?: number;
  startedAt?: Date;
  completedAt?: Date;
}

export interface Entry {
  _id?: string;
  date: string;
  type: 'checkin' | 'manual' | 'reflection';
  content: {
    title?: string;
    body: string;
    questions?: string[];
    responses?: string[];
  };
  metadata: {
    checkinType?: 'morning' | 'evening';
    wordCount?: number;
    tags?: string[];
    mood?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

class PersistenceService {
  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  private getAuthHeaders() {
    const token = this.getAuthToken();
    const headers: Record<string, string> = token ? { 'x-auth-token': token } : {};
    
    // Add access granted header if password protection was bypassed
    const accessGranted = localStorage.getItem('thinkmate_access_granted');
    if (accessGranted === 'true') {
      headers['x-access-granted'] = 'true';
    }
    
    // Add testing mode header if enabled
    const testingMode = localStorage.getItem('thinkmate_testing_mode');
    if (testingMode === 'true') {
      headers['x-testing-mode'] = 'true';
    }
    
    // Debug log in development
    if (this.isDevelopmentMode()) {
      console.log('🔧 Auth headers being sent:', {
        headers,
        accessGranted,
        hasToken: !!token,
        testingMode
      });
    }
    
    return headers;
  }

  private isOnline(): boolean {
    return navigator.onLine;
  }

  private isDevelopmentMode(): boolean {
    try {
      return process.env.NODE_ENV === 'development' || 
             (typeof window !== 'undefined' && window.location.hostname === 'localhost');
    } catch {
      return process.env.NODE_ENV === 'development';
    }
  }

  // Preferences Management
  async getPreferences(): Promise<UserPreferences> {
    // Always start with localStorage data as baseline
    const getLocalPreferences = (): UserPreferences => {
      const passwordGranted = localStorage.getItem('thinkmate_access_granted');
      const passwordGrantedAt = localStorage.getItem('thinkmate_access_granted_at');
      const momentumStatus = localStorage.getItem('momentum_last_seen_status');
      
      // Get timezone preferences
      const timezoneData = localStorage.getItem('thinkmate_timezone_preferences');
      let timezonePreferences: TimezonePreferences;
      try {
        timezonePreferences = timezoneData ? 
          validateTimezonePreferences(JSON.parse(timezoneData)) : 
          DEFAULT_TIMEZONE_PREFERENCES;
      } catch {
        timezonePreferences = DEFAULT_TIMEZONE_PREFERENCES;
      }
      
      // Get check-in completion data for today
      const today = new Date().toISOString().slice(0, 10);
      const morningKey = `morningCheckInComplete_${today}`;
      const eveningKey = `eveningCheckInComplete_${today}`;
      const morningComplete = localStorage.getItem(morningKey) === 'true';
      const eveningComplete = localStorage.getItem(eveningKey) === 'true';
      
      return {
        passwordProtection: passwordGranted ? {
          granted: passwordGranted === 'true',
          grantedAt: passwordGrantedAt ? new Date(parseInt(passwordGrantedAt)) : new Date()
        } : undefined,
        momentum: momentumStatus ? { lastSeenStatus: momentumStatus } : undefined,
        checkInCompletion: {
          morning: morningComplete,
          evening: eveningComplete,
          date: today
        },
        timezone: timezonePreferences
      };
    };

    try {
      // NEVER make API calls in development without auth token
      if (!this.getAuthToken()) {
        if (this.isDevelopmentMode()) {
          console.log('🔧 Dev mode: Skipping preferences API call (no auth token)');
        }
        return getLocalPreferences();
      }

      // If offline, return localStorage data
      if (!this.isOnline()) {
        return getLocalPreferences();
      }

      // Try to fetch from server with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await axios.get(`${API_URL}/api/user/preferences`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal,
        timeout: 5000
      });
      
      clearTimeout(timeoutId);
      return response.data.preferences || getLocalPreferences();
      
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
          console.warn('Preferences request timed out, using localStorage fallback');
        } else if (error.response?.status === 401) {
          console.warn('Authentication expired, clearing tokens and using localStorage');
          this.removeAuthToken();
          this.removeUserProfile();
        } else if (error.response?.status >= 500) {
          console.warn('Server error fetching preferences, using localStorage fallback:', error.response.status);
        } else {
          console.warn('Failed to fetch preferences from server:', error.message);
        }
      } else {
        console.warn('Network error fetching preferences:', error);
      }
      
      return getLocalPreferences();
    }
  }

  async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
    let localUpdateSuccess = false;
    
    try {
      // Always update localStorage for immediate access (most important)
      if (preferences.passwordProtection) {
        if (preferences.passwordProtection.granted) {
          localStorage.setItem('thinkmate_access_granted', 'true');
          localStorage.setItem('thinkmate_access_granted_at', preferences.passwordProtection.grantedAt.getTime().toString());
        } else {
          localStorage.removeItem('thinkmate_access_granted');
          localStorage.removeItem('thinkmate_access_granted_at');
        }
      }

      if (preferences.momentum?.lastSeenStatus) {
        localStorage.setItem('momentum_last_seen_status', preferences.momentum.lastSeenStatus);
      }

      if (preferences.timezone) {
        const validatedTimezone = validateTimezonePreferences(preferences.timezone);
        localStorage.setItem('thinkmate_timezone_preferences', JSON.stringify(validatedTimezone));
      }

      if (preferences.checkInCompletion) {
        const today = preferences.checkInCompletion.date || new Date().toISOString().slice(0, 10);
        const morningKey = `morningCheckInComplete_${today}`;
        const eveningKey = `eveningCheckInComplete_${today}`;
        
        if (preferences.checkInCompletion.morning !== undefined) {
          if (preferences.checkInCompletion.morning) {
            localStorage.setItem(morningKey, 'true');
          } else {
            localStorage.removeItem(morningKey);
          }
        }
        
        if (preferences.checkInCompletion.evening !== undefined) {
          if (preferences.checkInCompletion.evening) {
            localStorage.setItem(eveningKey, 'true');
          } else {
            localStorage.removeItem(eveningKey);
          }
        }
      }
      
      localUpdateSuccess = true;

      // If online and authenticated, try to sync to server
      if (this.isOnline() && this.getAuthToken()) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout for updates

        await axios.put(`${API_URL}/api/user/preferences`, 
          { preferences },
          { 
            headers: this.getAuthHeaders(),
            signal: controller.signal,
            timeout: 3000
          }
        );
        
        clearTimeout(timeoutId);
      }
    } catch (error) {
      if (!localUpdateSuccess) {
        // Local update failed - this is critical
        console.error('Critical: Failed to update local preferences:', error);
        throw new Error('Failed to save preferences locally');
      }
      
      // Server sync failed but local update succeeded
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
          console.warn('Preferences sync timed out - changes saved locally');
        } else if (error.response?.status === 401) {
          console.warn('Authentication expired during preferences sync');
          this.removeAuthToken();
          this.removeUserProfile();
        } else {
          console.warn('Failed to sync preferences to server:', error.response?.status || error.message);
        }
      } else {
        console.warn('Network error syncing preferences:', error);
      }
    }
  }

  // Onboarding Data Management
  async getOnboardingData(): Promise<OnboardingData> {
    // Always start with localStorage data as baseline
    const getLocalOnboardingData = (): OnboardingData => {
      const onboardingAnswers = localStorage.getItem('onboardingAnswers');
      const thinkmateOnboarded = localStorage.getItem('thinkmateOnboarded');
      const onboardingStarted = localStorage.getItem('onboardingStarted');
      
      return {
        answers: onboardingAnswers ? JSON.parse(onboardingAnswers) : [],
        currentStep: 0,
        startedAt: onboardingStarted ? new Date(onboardingStarted) : undefined,
        completedAt: thinkmateOnboarded ? new Date() : undefined
      };
    };

    try {
      // NEVER make API calls without auth token
      if (!this.getAuthToken()) {
        if (this.isDevelopmentMode()) {
          console.log('🔧 Dev mode: Skipping onboarding data API call (no auth token)');
        }
        return getLocalOnboardingData();
      }

      // If offline, return localStorage data
      if (!this.isOnline()) {
        return getLocalOnboardingData();
      }

      // Try to fetch from server with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await axios.get(`${API_URL}/api/user/onboarding-data`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal,
        timeout: 5000
      });
      
      clearTimeout(timeoutId);
      return response.data.onboardingData || getLocalOnboardingData();
      
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
          console.warn('Onboarding data request timed out, using localStorage fallback');
        } else if (error.response?.status === 401) {
          console.warn('Authentication expired, clearing tokens and using localStorage');
          this.removeAuthToken();
          this.removeUserProfile();
        } else {
          console.warn('Failed to fetch onboarding data from server:', error.message);
        }
      } else {
        console.warn('Network error fetching onboarding data:', error);
      }
      
      return getLocalOnboardingData();
    }
  }

  async updateOnboardingData(data: Partial<OnboardingData>): Promise<void> {
    try {
      // Always update localStorage for immediate access
      if (data.answers) {
        localStorage.setItem('onboardingAnswers', JSON.stringify(data.answers));
      }
      
      if (data.completedAt) {
        localStorage.setItem('thinkmateOnboarded', 'true');
      }

      if (data.startedAt) {
        localStorage.setItem('onboardingStarted', data.startedAt.toISOString());
      }

      // If online and authenticated, sync to server
      if (this.isOnline() && this.getAuthToken()) {
        await axios.put(`${API_URL}/api/user/onboarding-data`, 
          { onboardingData: data },
          { headers: this.getAuthHeaders() }
        );
      }
    } catch (error) {
      console.warn('Failed to sync onboarding data to server:', error);
      // localStorage already updated, so operation isn't lost
    }
  }

  // Authentication Token Management (keeping localStorage for now)
  getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  setAuthToken(token: string): void {
    localStorage.setItem('token', token);
  }

  removeAuthToken(): void {
    localStorage.removeItem('token');
  }

  // User Profile Management (keeping localStorage for now)
  getUserProfile(): any {
    const profile = localStorage.getItem('userProfile');
    return profile ? JSON.parse(profile) : null;
  }

  setUserProfile(profile: any): void {
    localStorage.setItem('userProfile', JSON.stringify(profile));
  }

  removeUserProfile(): void {
    localStorage.removeItem('userProfile');
  }

  // Check-in completion helper methods
  async getCheckInCompletion(): Promise<{morning: boolean, evening: boolean, date: string}> {
    const preferences = await this.getPreferences();
    return preferences.checkInCompletion || {
      morning: false,
      evening: false,
      date: new Date().toISOString().slice(0, 10)
    };
  }

  async setCheckInCompletion(type: 'morning' | 'evening', completed: boolean): Promise<void> {
    const today = new Date().toISOString().slice(0, 10);
    await this.updatePreferences({
      checkInCompletion: {
        [type]: completed,
        date: today
      }
    });
  }

  // Clear all data
  clearAllData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('onboardingData');
    localStorage.removeItem('onboardingAnswers');
    localStorage.removeItem('thinkmateOnboarded');
    localStorage.removeItem('onboardingStarted');
    localStorage.removeItem('thinkmate_access_granted');
    localStorage.removeItem('thinkmate_access_granted_at');
    localStorage.removeItem('momentum_last_seen_status');
    
    // Clear all check-in completion data
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('morningCheckInComplete_') || key.startsWith('eveningCheckInComplete_')) {
        localStorage.removeItem(key);
      }
    });
  }

  // Entries Management
  async getEntries(options: {
    page?: number;
    limit?: number;
    type?: 'checkin' | 'manual' | 'reflection';
    startDate?: string;
    endDate?: string;
  } = {}): Promise<{ entries: Entry[], pagination: any }> {
    
    if (this.isDevelopmentMode()) {
      console.log('🔧 getEntries called with options:', options);
      console.log('🔧 Auth token available:', !!this.getAuthToken());
      console.log('🔧 Online status:', this.isOnline());
    }
    
    // Always try API first if available (or in development with access granted)
    const hasAccessInDev = this.isDevelopmentMode() && localStorage.getItem('thinkmate_access_granted') === 'true';
    if ((this.getAuthToken() || hasAccessInDev) && this.isOnline()) {
      try {
        const queryParams = new URLSearchParams();
        if (options.page) queryParams.set('page', options.page.toString());
        if (options.limit) queryParams.set('limit', options.limit.toString());
        if (options.type) queryParams.set('type', options.type);
        if (options.startDate) queryParams.set('startDate', options.startDate);
        if (options.endDate) queryParams.set('endDate', options.endDate);

        const response = await axios.get(`${API_URL}/api/entries?${queryParams}`, {
          headers: this.getAuthHeaders(),
          timeout: 5000
        });

        return response.data;
      } catch (error) {
        console.warn('API entries fetch failed, falling back to localStorage:', error);
      }
    }

    // Fallback: Reconstruct entries from localStorage check-in responses
    return this.getEntriesFromLocalStorage(options);
  }

  private getEntriesFromLocalStorage(options: {
    page?: number;
    limit?: number;
    type?: 'checkin' | 'manual' | 'reflection';
    startDate?: string;
    endDate?: string;
  }): { entries: Entry[], pagination: any } {
    const entries: Entry[] = [];
    
    // Cache localStorage keys to avoid repeated Object.keys() calls
    const cachedKeys = this.getStorageKeys();
    
    // Find all check-in response keys with optimized filtering
    const responseKeys = cachedKeys.filter(key => key.endsWith('_responses'));
    
    // Batch localStorage reads for better performance
    const responsesData = this.batchGetLocalStorageItems(responseKeys);
    
    for (const [key, rawData] of responsesData) {
      try {
        const responses = JSON.parse(rawData || '[]');
        if (!Array.isArray(responses) || responses.length === 0) continue;

        // Extract date and type from key format: checkin_morning_20240728_responses
        const keyParts = key.replace('_responses', '').split('_');
        if (keyParts.length < 3) continue;

        const checkinType = keyParts[1]; // 'morning' or 'evening'
        const dateStr = keyParts[2]; // '20240728'
        
        // Convert to proper date format
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(4, 6);
        const day = dateStr.slice(6, 8);
        const date = `${year}-${month}-${day}`;

        // Get questions (reconstructed from ConversationalCheckIn)
        const questions = [
          "What's your highest priority for today?",
          "Any worries or potential obstacles to your progress?",
          "What's one small step you can take today to build momentum?"
        ];

        // Create entry
        const entry: Entry = {
          _id: `local_${key}`,
          date,
          type: 'checkin',
          content: {
            title: `${checkinType.charAt(0).toUpperCase() + checkinType.slice(1)} Check-in`,
            body: questions.map((q, i) => `Q: ${q}\nA: ${responses[i] || ''}`).join('\n\n'),
            questions,
            responses
          },
          metadata: {
            checkinType: checkinType as 'morning' | 'evening',
            wordCount: this.calculateWordCount(responses.join(' '))
          },
          createdAt: date,
          updatedAt: date
        };

        entries.push(entry);
      } catch (error) {
        console.warn(`Failed to parse entry from ${key}:`, error);
      }
    }

    // Apply filters
    let filteredEntries = entries;
    
    if (options.type) {
      filteredEntries = filteredEntries.filter(e => e.type === options.type);
    }
    
    if (options.startDate) {
      filteredEntries = filteredEntries.filter(e => e.date >= options.startDate!);
    }
    
    if (options.endDate) {
      filteredEntries = filteredEntries.filter(e => e.date <= options.endDate!);
    }

    // Sort by date (newest first)
    filteredEntries.sort((a, b) => b.date.localeCompare(a.date));

    // Apply pagination
    const page = options.page || 1;
    const limit = options.limit || 20;
    const start = (page - 1) * limit;
    const paginatedEntries = filteredEntries.slice(start, start + limit);

    return {
      entries: paginatedEntries,
      pagination: {
        page,
        limit,
        total: filteredEntries.length,
        pages: Math.ceil(filteredEntries.length / limit),
        hasNext: start + limit < filteredEntries.length,
        hasPrev: page > 1
      }
    };
  }

  async createEntry(entry: Omit<Entry, '_id' | 'createdAt' | 'updatedAt'>): Promise<Entry> {
    // Try API first
    if (this.getAuthToken() && this.isOnline()) {
      try {
        const response = await axios.post(`${API_URL}/api/entries`, entry, {
          headers: this.getAuthHeaders(),
          timeout: 5000
        });
        return response.data;
      } catch (error) {
        console.warn('API entry creation failed, storing locally:', error);
      }
    }

    // Fallback: Store in localStorage (manual entries only)
    if (entry.type === 'manual' || entry.type === 'reflection') {
      const localEntry: Entry = {
        ...entry,
        _id: `local_${Date.now()}_${Math.random()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const storageKey = `manual_entry_${localEntry._id}`;
      localStorage.setItem(storageKey, JSON.stringify(localEntry));
      return localEntry;
    }

    throw new Error('Cannot create check-in entries manually');
  }

  async createEntryFromCheckin(responses: string[], questions: string[], checkinType: 'morning' | 'evening', date?: string): Promise<Entry> {
    const entryDate = date || new Date().toISOString().slice(0, 10);

    // Try API first
    if (this.getAuthToken() && this.isOnline()) {
      try {
        const response = await axios.post(`${API_URL}/api/entries/from-checkin`, {
          responses,
          questions,
          checkinType,
          date: entryDate
        }, {
          headers: this.getAuthHeaders(),
          timeout: 5000
        });
        return response.data;
      } catch (error) {
        console.warn('API checkin entry creation failed:', error);
      }
    }

    // Fallback: Entry is already stored in localStorage by ConversationalCheckIn
    // Just return a constructed entry object
    const entry: Entry = {
      _id: `local_checkin_${checkinType}_${entryDate.replace(/-/g, '')}`,
      date: entryDate,
      type: 'checkin',
      content: {
        title: `${checkinType.charAt(0).toUpperCase() + checkinType.slice(1)} Check-in`,
        body: questions.map((q, i) => `Q: ${q}\nA: ${responses[i] || ''}`).join('\n\n'),
        questions,
        responses
      },
      metadata: {
        checkinType,
        wordCount: this.calculateWordCount(responses.join(' '))
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return entry;
  }

  // Performance optimization helpers
  private storageKeysCache: string[] | null = null;
  private storageKeysCacheTime = 0;
  private readonly CACHE_DURATION = 5000; // 5 seconds

  private getStorageKeys(): string[] {
    const now = Date.now();
    if (this.storageKeysCache && (now - this.storageKeysCacheTime) < this.CACHE_DURATION) {
      return this.storageKeysCache;
    }
    
    this.storageKeysCache = Object.keys(localStorage);
    this.storageKeysCacheTime = now;
    return this.storageKeysCache;
  }

  private batchGetLocalStorageItems(keys: string[]): Array<[string, string | null]> {
    return keys.map(key => [key, localStorage.getItem(key)]);
  }

  private calculateWordCount(text: string): number {
    const trimmed = text.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  }

  // Testing mode helpers
  enableTestingMode(): void {
    localStorage.setItem('thinkmate_testing_mode', 'true');
    console.log('🧪 Testing mode enabled - Multiple check-ins allowed');
  }

  disableTestingMode(): void {
    localStorage.removeItem('thinkmate_testing_mode');
    console.log('🧪 Testing mode disabled - Normal check-in validation restored');
  }

  isTestingModeEnabled(): boolean {
    return localStorage.getItem('thinkmate_testing_mode') === 'true';
  }
}

export const persistenceService = new PersistenceService();
export default persistenceService;